import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";

// Persistent WebSocket singleton lifted out of Layout so it survives route
// changes (no more mount/unmount churn on every navigation).
// Exposes:
//   • useAlerts(cb)      → cb runs on every {type:"alert"} message
//   • usePriceTicks()    → returns latest prices map {SYMBOL:{p, ch, asset_type, ticker, t}}
//   • useLiveStream()    → connection status + raw send()

const Ctx = createContext(null);

export const LiveStreamProvider = ({ children }) => {
  const [status, setStatus] = useState("closed");   // closed|connecting|open
  const [prices, setPrices] = useState({});          // symbol -> {p, ch, asset_type, ticker, t, prev}
  const wsRef = useRef(null);
  const retryRef = useRef(0);
  const stoppedRef = useRef(false);
  const alertHandlersRef = useRef(new Set());

  const connect = useCallback(() => {
    if (stoppedRef.current) return;
    const token = localStorage.getItem("token");
    if (!token) return;
    const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/^http/, "ws");
    const url = `${base}/api/ws?token=${encodeURIComponent(token)}`;
    setStatus("connecting");
    try {
      const ws = new WebSocket(url);
      wsRef.current = ws;
      ws.onopen = () => { setStatus("open"); retryRef.current = 0; };
      ws.onmessage = (ev) => {
        let msg = null;
        try { msg = JSON.parse(ev.data); } catch { return; }
        if (!msg || !msg.type) return;
        if (msg.type === "alert") {
          for (const h of alertHandlersRef.current) { try { h(msg); } catch {} }
        } else if (msg.type === "prices" && msg.prices) {
          setPrices((prev) => {
            const next = { ...prev };
            for (const [sym, info] of Object.entries(msg.prices)) {
              const before = next[sym];
              next[sym] = {
                ...info,
                t: msg.t,
                prev: before ? before.p : undefined,
              };
            }
            return next;
          });
        }
      };
      ws.onclose = () => {
        wsRef.current = null;
        setStatus("closed");
        if (stoppedRef.current) return;
        const delay = Math.min(30000, 1000 * Math.pow(2, retryRef.current++));
        setTimeout(connect, delay);
      };
      ws.onerror = () => { try { ws.close(); } catch {} };
    } catch {
      const delay = Math.min(30000, 1000 * Math.pow(2, retryRef.current++));
      setTimeout(connect, delay);
    }
  }, []);

  useEffect(() => {
    stoppedRef.current = false;
    connect();
    const iv = setInterval(() => {
      const ws = wsRef.current;
      if (ws && ws.readyState === 1) try { ws.send("ping"); } catch {}
    }, 25000);
    // Reconnect when the tab wakes up
    const onVisible = () => {
      if (document.visibilityState === "visible" && (!wsRef.current || wsRef.current.readyState !== 1)) {
        connect();
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      stoppedRef.current = true;
      clearInterval(iv);
      document.removeEventListener("visibilitychange", onVisible);
      try { wsRef.current?.close(); } catch {}
    };
  }, [connect]);

  const send = useCallback((msg) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === 1) try { ws.send(typeof msg === "string" ? msg : JSON.stringify(msg)); } catch {}
  }, []);

  const subscribeAlert = useCallback((handler) => {
    alertHandlersRef.current.add(handler);
    return () => alertHandlersRef.current.delete(handler);
  }, []);

  return (
    <Ctx.Provider value={{ status, prices, send, subscribeAlert }}>
      {children}
    </Ctx.Provider>
  );
};

export const useLiveStream = () => {
  const ctx = useContext(Ctx);
  return ctx || { status: "closed", prices: {}, send: () => {}, subscribeAlert: () => () => {} };
};

// Subscribe to alerts as a side-effect. `handler` receives the raw {type:"alert", ...} message.
export const useAlerts = (handler) => {
  const ctx = useContext(Ctx);
  useEffect(() => {
    if (!ctx || !handler) return;
    return ctx.subscribeAlert(handler);
  }, [ctx, handler]);
};

// Returns the latest price map (auto-rerenders on every tick).
export const usePriceTicks = () => {
  const ctx = useContext(Ctx);
  return ctx ? ctx.prices : {};
};

// Returns { fresh, oldest_seconds }: whether the tick data across the given
// symbols is fresh enough (fetched_at within the last `staleSeconds`). Used
// to render a subtle "prices delayed" pill so users know when upstream
// providers (yfinance, CoinGecko) are throttling us and the WS is broadcasting
// cached values instead of true real-time data.
export const useMarketFreshness = (symbols, staleSeconds = 90) => {
  const prices = usePriceTicks();
  const now = Date.now();
  let oldestMs = 0;
  const list = Array.isArray(symbols) ? symbols : Object.keys(prices);
  for (const item of list) {
    const sym = typeof item === "string" ? item : item?.symbol;
    if (!sym) continue;
    const fa = prices[sym]?.fa;
    if (!fa) continue;
    const ageMs = now - new Date(fa).getTime();
    if (ageMs > oldestMs) oldestMs = ageMs;
  }
  const oldest_seconds = Math.round(oldestMs / 1000);
  return { fresh: oldest_seconds <= staleSeconds, oldest_seconds };
};

// Subscribe to ad-hoc symbol ticks for the duration of the caller component's
// lifetime. Backend price-ticker loop unions these with watchlist/positions
// symbols so pages like Portfolio, Markets, or BotDashboard get live updates
// even when the ticker isn't in the user's persistent watchlist.
//   symbols: [{symbol: "AAPL", asset_type: "stock"}, ...]
// The list is diffed against the previous mount so React re-runs cleanly when
// props change (e.g. positions load asynchronously).
export const useSubscribePrices = (symbols) => {
  const ctx = useContext(Ctx);
  const status = ctx?.status;
  const send = ctx?.send;
  // Stable serialization so array identity doesn't cause useEffect churn
  const key = JSON.stringify(
    (symbols || [])
      .filter((s) => s && s.symbol)
      .map((s) => [s.asset_type || "stock", s.symbol])
      .sort()
  );
  useEffect(() => {
    if (!send || status !== "open") return;
    const parsed = JSON.parse(key);
    if (!parsed.length) return;
    const payload = parsed.map(([asset_type, symbol]) => ({ asset_type, symbol }));
    send({ type: "subscribe", symbols: payload });
    return () => {
      // Best-effort — if socket is closed, subs are already dropped server-side
      try { send({ type: "unsubscribe", symbols: payload }); } catch {}
    };
  }, [key, send, status]);
};
